import React, { useContext, useEffect, useMemo, useState } from "react"
import { AdminContext } from "~/contexts/AdminContext"
import { ReservationProps, SiteProps } from "../../../interfaces"
import { adminPages } from "~/types/adminPages"
import Api2 from "../../../api2"
import { KINDS, longTermReservationKind, RESERVATION_KINDS } from "~/constants"
import moment, { Moment } from "moment"
import Table, { RowItem } from "./Table"
import { FormProvider } from "~/contexts/FormContext"
import EditInvoice from "./EditInvoice"
import IInvoice from "../../../interfaces/IInvoice"
import { filterOptions, INVOICE_FILTER_OPTIONS } from "./InvoiceFilterOptions"
import { useDebouncedCallback } from "use-debounce"
import { INVOICE_STATUSES } from "~/decorators/InvoiceDecorator"
import roundToMaxPrecision from "../../../utils/roundToMaxPrecision"
import { invoiceFilterCookie } from "~/constants/cookies"
import { useCookies } from "react-cookie"
import Button from "../../ui/Button"
import { validKinds } from "~/types"
import { bySort } from "~/utils/sorts"
import { IOption } from "../../ui/Admin/OptionSelector"
import { isMobile } from "react-device-detect"
import { tSortableColumns } from "~/api2/invoices"

export const mapInvoiceToRowItem = (invoice: IInvoice): RowItem => ({
  isSiteOnly: false,
  amountOwed: invoice.order?.total_in_cents / 100,
  status: invoice.status,
  invoice,
  invoiceId: invoice.id,
  site: invoice.reservation?.site,
  siteId: invoice.reservation?.site_id,
  siteName: invoice.reservation?.site?.name,
  siteSort: invoice.reservation?.site?.sort,
  reservation: invoice.reservation,
  reservationId: invoice.reservation?.id,
  reservationStartDate: moment(invoice.reservation?.start_date),
  reservationEndDate: moment(invoice.reservation?.end_date),
  reservationRecurringKind:
    invoice.reservation?.kind === null && invoice.reservation.long_term_reservation_id
      ? "seasonal"
      : invoice.reservation?.kind == RESERVATION_KINDS.FINAL_INVOICE
        ? "final_invoice"
        : invoice.reservation?.long_term_reservation?.kind,
  camperName:
    [
      invoice?.reservation?.user?.camper?.first_name || invoice.reservation?.user?.first_name,
      invoice?.reservation?.user?.camper?.last_name || invoice.reservation?.user?.last_name,
    ]
      .filter((str) => str)
      .join(" ") || invoice.recipient_name,
  lastMeterReading: invoice.reservation?.last_meter_reading || invoice.reservation?.site?.last_meter_reading,
  currentMeterReading: invoice.reservation?.meter_reading || invoice.reservation?.site?.last_meter_reading,
  electricUsed: roundToMaxPrecision(
    Number(invoice.reservation?.meter_reading) - Number(invoice.reservation?.last_meter_reading),
  ),
  hasMeterLocationId: !!(
    invoice.reservation?.site?.wild_energy_meter_location_id ??
    invoice?.reservation?.site?.wild_energy_meter_location_id
  ),
  isSyncingMeterReading: false,
  electricSubtotal: electricSubtotalFor(invoice),
  invoiceSendDate: moment(invoice.send_date),
  invoiceDueDate: moment(invoice.due_date),
})

const electricSubtotalFor = (invoice: IInvoice): number => {
  const electricItems = invoice.order.line_items.filter((item) => item.kind === "electric")
  const total = electricItems.reduce((sum, item) => sum + item.total_amount_in_cents, 0)
  if (electricItems) {
    return total
  } else {
    return invoice.reservation?.electricity_fee_in_cents / 100
  }
}

export interface IInvoicesForm {
  electric_rate: number
  rowItems: RowItem[]
}

/** @deprecated */
export type iInvoice = ReservationProps & {
  invoice_status: INVOICE_STATUSES
  invoice_date: string
  recurring_price_in_cents: number
  electric_used: number
  last_meter_reading: number
  recurring_kind: longTermReservationKind
  is_site_only: boolean
}

const mapFilterName = (filter: string) => {
  switch (filter) {
    case "start_date":
      return "reservation_start_date"
    case "end_date":
      return "reservation_end_date"
    default:
      return null
  }
}

interface ComponentProps {
  setPage: (page: adminPages) => void // TODO:
  setSelectedReservation: (reservation: ReservationProps) => void
}

const mapSiteToRowItem = (site: SiteProps): RowItem => ({
  isSiteOnly: true,
  amountOwed: null,
  invoice: null,
  status: null,
  invoiceId: null,
  site,
  siteId: site.id,
  siteName: site.name,
  siteSort: site.sort,
  reservation: null,
  reservationId: null,
  reservationStartDate: null,
  reservationEndDate: null,
  reservationRecurringKind: null,
  camperName: null,
  lastMeterReading: site.last_meter_reading,
  currentMeterReading: site.last_meter_reading,
  electricUsed: 0,
  electricSubtotal: 0,
  invoiceSendDate: null,
  invoiceDueDate: null,
})

const InvoicesPage = (props: ComponentProps) => {
  const { camp, sites, campingStyles, updateLocalReservations, reservationStates } = useContext(AdminContext)
  const [rowItems, setRowItems] = useState<RowItem[]>([])
  const [selectedInvoice, setSelectedInvoice] = useState<IInvoice>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [isShowingAllSites, setIsShowingAllSites] = useState(false)
  const [selectedStartDate, setSelectedStartDate] = useState<Moment>(moment())
  const [selectedEndDate, setSelectedEndDate] = useState<Moment>(moment().add(1, "month"))
  const [selectedInterval, setSelectedInterval] = useState<string>("Next month")
  const [fallbackSelectedInterval, setFallbackSelectedInterval] = useState<string>("Next month")
  const [cookies, setCookie] = useCookies([invoiceFilterCookie])
  const [selectedFilterBy, setSelectedFilterBy] = useState<string>(cookies[invoiceFilterCookie] || "due_date")
  const [sortBy, setSortBy] = useState<tSortableColumns>("invoice_date")
  const [selectedFilterOption, setSelectedFilterOption] = useState<IOption<INVOICE_FILTER_OPTIONS>>(filterOptions[0])
  const [isAscending, setIsAscending] = useState(false)
  const [totalRecords, setTotalRecords] = useState<number>(0)
  const [page, setPage] = useState<number>(1)
  const [perPage, setPerPage] = useState<number>(50)
  const [isMeterFieldsEditable, setIsMeterFieldsEditable] = useState(false)

  const updateDates = (start: Moment, end: Moment) => {
    setSelectedStartDate(start)
    setSelectedEndDate(end)
  }

  const onChangeSelectedInterval = (interval: string) => {
    let start: Moment = moment()
    let end: Moment = moment()

    switch (interval) {
      case "Last month":
        start = moment().subtract(1, "month").date(moment().date())
        end = moment()
        updateDates(start, end)
        break

      case "Next 2 weeks":
        start = moment()
        end = moment().add(2, "weeks")
        updateDates(start, end)
        break
      case "Next month":
        start = moment()
        end = moment().add(1, "month")
        updateDates(start, end)
        break

      case "Year to date":
        start = moment().startOf("year")
        end = moment()
        updateDates(start, end)
        break
      case "All time":
        start = moment(camp.created_at)
        end = moment().add(1, "year")
        updateDates(start, end)
        break
      case "January":
        start = moment().startOf("year").month(0)
        end = moment().startOf("year").month(1).subtract(1, "day")
        updateDates(start, end)
        break
      case "February":
        start = moment().startOf("year").month(1)
        end = moment().startOf("year").month(2).subtract(1, "day")
        updateDates(start, end)
        break
      case "March":
        start = moment().startOf("year").month(2)
        end = moment().startOf("year").month(3).subtract(1, "day")
        updateDates(start, end)
        break
      case "April":
        start = moment().startOf("year").month(3)
        end = moment().startOf("year").month(4).subtract(1, "day")
        updateDates(start, end)
        break
      case "May":
        start = moment().startOf("year").month(4)
        end = moment().startOf("year").month(5).subtract(1, "day")
        updateDates(start, end)
        break
      case "June":
        start = moment().startOf("year").month(5)
        end = moment().startOf("year").month(6).subtract(1, "day")
        updateDates(start, end)
        break
      case "July":
        start = moment().startOf("year").month(6)
        end = moment().startOf("year").month(7).subtract(1, "day")
        updateDates(start, end)
        break
      case "August":
        start = moment().startOf("year").month(7)
        end = moment().startOf("year").month(8).subtract(1, "day")
        updateDates(start, end)
        break
      case "September":
        start = moment().startOf("year").month(8)
        end = moment().startOf("year").month(9).subtract(1, "day")
        updateDates(start, end)
        break
      case "October":
        start = moment().startOf("year").month(9)
        end = moment().startOf("year").month(10).subtract(1, "day")
        updateDates(start, end)
        break
      case "November":
        start = moment().startOf("year").month(10)
        end = moment().startOf("year").month(11).subtract(1, "day")
        updateDates(start, end)
        break
      case "December":
        start = moment().startOf("year").month(11)
        end = moment().startOf("year").month(12).subtract(1, "day")
        updateDates(start, end)
        break
      case "Custom":
        break
      default:
        break
    }
  }

  const applySites = (items: RowItem[]): RowItem[] => {
    if (!isShowingAllSites || !isMeterFieldsEditable) return items.filter((item) => item.invoiceId)

    const electricSiteKinds = [KINDS.RV_BACKIN, KINDS.RV_HYBRID, KINDS.RV_PULLTHROUGH, KINDS.BOAT_SLIP] as validKinds[]

    return sites
      .filter((site) => electricSiteKinds.includes(site.kind))
      .sort(bySort)
      .flatMap((site) => {
        const siteItems = items.filter((item) => item.siteId === site.id)
        return siteItems.length ? siteItems : mapSiteToRowItem(site)
      })
  }

  useEffect(() => {
    setPage(1)
  }, [selectedFilterOption])

  useEffect(() => {
    setRowItems(applySites(rowItems))
  }, [isShowingAllSites])

  useEffect(() => {
    if (!(selectedStartDate && selectedEndDate)) return
    setIsLoading(true)

    fetchInvoices()
  }, [selectedEndDate, selectedFilterBy, selectedFilterOption, reservationStates, page, sortBy, isAscending])
  // TODO: ^useEffect on `reservationStates` not best experience, since it'll refresh unecessarily
  // if online reservation comes in, for example, but best we can do since it's difficult to pass
  // callback functions to the showPaymentModal function. We should optimize this.

  const fetchInvoices = useDebouncedCallback(() => {
    if (!(selectedStartDate && selectedEndDate)) {
      setIsLoading(false)
      return
    }

    Api2.Invoices.fetchForDates(
      camp.id,
      {
        from: selectedStartDate,
        until: selectedEndDate,
        filter: mapFilterName(selectedFilterBy),
        statusFilter: selectedFilterOption.value,
        sortBy: sortBy,
        sortOrder: isAscending ? "asc" : "desc",
        includeArchived: selectedFilterOption.value === INVOICE_FILTER_OPTIONS.ARCHIVED,
      },
      page,
    )
      .then(({ invoices, total_count }: { invoices: IInvoice[]; total_count: number }) => {
        setRowItems(applySites(invoices.map(mapInvoiceToRowItem)))
        setIsLoading(false)
        setTotalRecords(total_count)
      })
      .catch((err) => {
        setIsLoading(false)
      })
  }, 100)

  const refreshLocalInvoices = () => {
    fetchInvoices()
  }

  return (
    <div className="flex-grow sm:mx-0 ">
      {selectedInvoice ? (
        <>
          <EditInvoice
            invoice={selectedInvoice}
            setSelectedInvoice={setSelectedInvoice}
            updateLocalInvoices={refreshLocalInvoices}
            disabled={false}
            site={selectedInvoice.reservation?.site}
            campingStyle={campingStyles.find((cs) => cs.id === selectedInvoice.reservation?.site?.camping_style_id)}
            isProcessing={isProcessing}
            customerView={false}
          />
        </>
      ) : (
        <>
          <div className="text-2xl font-semibold">Invoices</div>
          <MemoizedFormProvider electric_rate={camp?.electricity_rate} rowItems={rowItems}>
            <Table
              updateDates={updateDates}
              onChangeSelectedInterval={onChangeSelectedInterval}
              fallbackSelectedInterval={fallbackSelectedInterval}
              setFallbackSelectedInterval={setFallbackSelectedInterval}
              rowItems={rowItems}
              setRowItems={setRowItems}
              setSelectedInvoice={setSelectedInvoice}
              isShowingAllSites={isShowingAllSites}
              refreshLocalInvoices={refreshLocalInvoices}
              setIsShowingAllSites={setIsShowingAllSites}
              selectedStartDate={selectedStartDate}
              setSelectedStartDate={setSelectedStartDate}
              selectedEndDate={selectedEndDate}
              setSelectedEndDate={setSelectedEndDate}
              selectedInterval={selectedInterval}
              setSelectedInterval={setSelectedInterval}
              sortBy={sortBy}
              setSortBy={setSortBy}
              isAscending={isAscending}
              setIsAscending={setIsAscending}
              isLoading={isLoading}
              selectedFilterOption={selectedFilterOption}
              setSelectedFilterOption={setSelectedFilterOption}
              selectedFilterBy={selectedFilterBy}
              setSelectedFilterBy={setSelectedFilterBy}
              isMeterFieldsEditable={isMeterFieldsEditable}
              setIsMeterFieldsEditable={setIsMeterFieldsEditable}
              applySites={applySites}
            />
          </MemoizedFormProvider>
          <nav
            className="flex items-center mt-4 md:mt-0 justify-between border-t border-gray-200 pt-4 pb-14"
            aria-label="Pagination"
          >
            {totalRecords > 0 && (
              <div className="block pb-10 xsm:pb-0">
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{perPage * page - perPage + 1}</span> to{" "}
                  <span className="font-medium">{Math.min(perPage * page, totalRecords)}</span> of{" "}
                  <span className="font-medium">{totalRecords}</span> results
                </p>
              </div>
            )}
            {totalRecords > perPage && (
              <div className="flex xsm:flex-1 justify-between sm:justify-end pb-4">
                <Button
                  variant="white"
                  onClick={() => {
                    if (page > 1) setPage(page - 1)
                  }}
                  disabled={page <= 1}
                  className="w-24 justify-center ml-3"
                >
                  Previous
                </Button>
                <Button
                  variant="white"
                  onClick={() => {
                    if (page < totalRecords / perPage) setPage(page + 1)
                  }}
                  disabled={page >= totalRecords / perPage}
                  className="w-24 justify-center ml-3"
                >
                  Next
                </Button>
              </div>
            )}
          </nav>
        </>
      )}
    </div>
  )
}

const MemoizedFormProvider = ({ children, electric_rate, rowItems }) => {
  const memoizedValue = useMemo(
    () => ({
      electric_rate,
      rowItems,
    }),
    [electric_rate, rowItems],
  )

  return <FormProvider init={memoizedValue}>{children}</FormProvider>
}

export default InvoicesPage
